import { OptionalInherited } from '@nexspec/warehouse-shared-types/src/titles/TitleMetadata/Inherited';
import { Genre } from '@nexspec/warehouse-shared-types/src/titles/TitleMetadata/LocalizedInfo';
import { isInherited, titlePaths } from '@warehouse/title/core';
import React, { useCallback, useMemo, useState } from 'react';
import { hostServiceSingleton } from '@warehouse/shared/infra';
import { DropdownV2 } from '../../../shared/ui/dropdown';
import useTitleAutoSave from '../../../../src/views/title/hooks/useTitleAutoSave/useTitleAutoSave';
import genresJSON from '../../../../src/assets/json-administration-profiles/availableGenres.json';
import sortAlphabeticallyByLabel from '../../../../src/utils/sortAlphabeticallyByLabel';
import TitleEditableField from '../../../../src/components/titles/TitleEditableField';
import { ChipDisplayWrapper } from '../../../../src/views/title/tab/productMetadata/style';
import ChipDisplay from '../../../../src/components/library/ChipDisplay';

interface DropdownOption {
	value: string;
	label: string;
}

export function GenresField({ localizedInfoUuid }: { localizedInfoUuid: string }) {
	const [customGenres, setCustomGenres] = useState<string[]>([]);
	const domain = useMemo(() => hostServiceSingleton.get().getHostname(), []);

	const genres = useTitleAutoSave<OptionalInherited<Genre[]>>({
		label: 'Genres',
		path: useMemo(() => titlePaths.genres(localizedInfoUuid).split('.'), [localizedInfoUuid]),
	});

	const genresData = useMemo(() => genresJSON.sort(sortAlphabeticallyByLabel), []);

	const allGenreOptions = useMemo(() => {
		const predefinedOptions = genresData.map((genre) => ({
			value: genre.uuid,
			label: genre.label,
			isPickList: false,
		}));

		const currentCustomGenres = (genres?.value?.displayValue || [])
			.filter((item) => item.source !== 'nexspec.com/warehouse' && item.source !== undefined)
			.map((item) => item.genre);

		const allCustomGenres = [...new Set([...customGenres, ...currentCustomGenres])];

		const customOptions = allCustomGenres.map((genre) => ({
			value: genre,
			label: genre,
			isPickList: true,
		}));

		const allOptions = [...predefinedOptions, ...customOptions];
		return allOptions.sort((a, b) => a.label.localeCompare(b.label));
	}, [genresData, customGenres, genres?.value?.displayValue]);

	const inheritedResult = useMemo(() => isInherited({ inheritedObject: genres.value }), [genres.value]);

	const dropdownValue = useMemo(
		() => genres?.value?.displayValue?.map((i) => i.id ?? i.genre) ?? [],
		[genres?.value?.displayValue],
	);

	const chipDisplayValues = useMemo(
		() =>
			genres?.readOnlyValue?.displayValue
				?.sort((a, b) => (a.level ?? 0) - (b.level ?? 0))
				.map((genre) => {
					const isCustom = genre.source !== 'nexspec.com/warehouse' && genre.source !== undefined;

					let label: string;
					if (isCustom) {
						label = genre.genre || genre.id || '';
					} else {
						const predefinedGenre = genresData.find((g) => genre.id === g.uuid);
						label = predefinedGenre ? predefinedGenre.label : genre.id || '';
					}

					return {
						value: label,
						isCustom,
					};
				}) || [],
		[genres?.readOnlyValue?.displayValue, genresData],
	);

	const updateCustomGenresState = useCallback((selectedCustomGenres: string[]) => {
		setCustomGenres((prev) => {
			const newGenres = selectedCustomGenres.filter((val) => !prev.includes(val));
			const remainingGenres = prev.filter((genre) => selectedCustomGenres.includes(genre));
			return newGenres.length > 0 ? [...remainingGenres, ...newGenres] : remainingGenres;
		});
	}, []);

	const createGenreItem = useCallback(
		(val: string, index: number) => {
			const predefinedGenre = genresData.find((g) => g.uuid === val);
			if (predefinedGenre) {
				return {
					genre: predefinedGenre.uuid,
					id: predefinedGenre.uuid,
					source: 'nexspec.com/warehouse',
					level: index + 1,
				};
			}
			return {
				genre: val,
				id: val,
				source: `${domain}`,
				level: index + 1,
			};
		},
		[genresData, domain],
	);

	const updateGenresValue = useCallback(
		(updatedItems: Genre[]) => {
			genres?.setValue((prev): OptionalInherited<Genre[]> => {
				const hasToReInherit = !updatedItems.length;
				return {
					...prev,
					displayValue: hasToReInherit ? (prev?.inherited.value ?? []) : updatedItems,
				};
			});
		},
		[genres],
	);

	const handleChange = useCallback(
		(currentValues: string[]) => {
			// Filter for custom genres (ones not in predefined list)
			const selectedCustomGenres = currentValues.filter((val) => !genresData.some((g) => g.uuid === val));

			// Update custom genres state
			updateCustomGenresState(selectedCustomGenres);

			// Create genre items with proper structure
			const updatedItems = currentValues.map((val, index) => createGenreItem(val, index));

			// Update the genres value
			updateGenresValue(updatedItems);
		},
		[genresData, updateCustomGenresState, createGenreItem, updateGenresValue],
	);

	return (
		<TitleEditableField
			label="Genres"
			path={titlePaths.genres(localizedInfoUuid)}
			direction="column"
			readComponent={
				<ChipDisplayWrapper>
					<ChipDisplay
						values={chipDisplayValues}
						width={400}
						height={26}
						variant={inheritedResult.isInherited ? 'prefilled' : 'default'}
					/>
				</ChipDisplayWrapper>
			}
			editComponent={
				<DropdownV2<string[], DropdownOption>
					ariaLabel="Genres"
					disabledMargin
					variant={inheritedResult.isInherited ? 'prefilled' : 'default'}
					onBlur={() => genres?.commit()}
					onChange={handleChange}
					pickList
					withSearch
					searchPlaceholder="Search genres or add"
					options={allGenreOptions}
					value={dropdownValue}
					height={36}
					mode="multiple"
				/>
			}
		/>
	);
}
